
/* eslint-disable @typescript-eslint/camelcase */
import { Options, prop, Vue } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import { HubController } from "@/app/ui/controllers/HubController";
import { Hub, HubList, RequestHubList } from "@/domain/entities/Hub";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { OptionsClass } from "@/domain/entities/MainApp";
import MultipleSelect from "@/app/ui/components/multiple-select/index.vue";
import { LocationController } from "@/app/ui/controllers/LocationController";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import moment from "moment";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import MultipleSelectSearchVirtualScroll from "@/app/ui/components/multiple-select-search-virtual-scroll/index.vue";
import { CargoController } from "../../../controllers/CargoController";
import { DownloadCargoListApiRequest } from "@/data/payload/api/CargoApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import {
  formatLocalizedDate,
  parseFuzzyLocalizedDate
} from "@/app/infrastructures/misc/common-library/AutocorrectDate";
import { ReportController } from "@/app/ui/controllers/ReportController";
import { cloneDeep } from "lodash";
import debounce from "lodash/debounce";
import { UserController } from "@/app/ui/controllers/UserController";
import { RequestListUser } from "@/data/payload/api/UserApiRequest";
import { UserEntities, UserData } from "@/domain/entities/User";
import DropdownMultiSelect from "@/app/ui/components/dropdown-multi-select/index.vue";
import { CARGO_TYPE_OPTIONS } from "../modules/cargo-module";

moment.locale(
  AccountController.accountData.countryCode === "my"
    ? "en"
    : AccountController.accountData.countryCode || "id"
);


class Props {
  modelValue = prop<boolean>({
    default: false,
    type: Boolean,
    required: true
  });
}

@Options({
  components: {
    OverlayPanel,
    MultipleSelect,
    SelectSearch,
    MultipleSelectSearchVirtualScroll,
    DropdownMultiSelect
  },
  watch: {
    modelValue(val: boolean) {
      if (val) {
        this.init();
      }
    }
  }
})
export default class ModalDownloadListCargo extends Vue.with(Props) {
  get visible() {
    return this.modelValue;
  }
  set visible(val: boolean) {
    this.$emit("update:modelValue", val);
  }
  get modaOptions() {
    return CARGO_TYPE_OPTIONS.map(
      (item) =>
        new OptionsClass({
          label: item.name,
          value: item.value
        })
    );
  }
  onClose() {
    this.visible = false;
  }

  // form
  form = {
    periodeStart: new Date(),
    periodeEnd: new Date(),
    periodeStartTime: new Date(),
    periodeEndTime: new Date(),
    selectedHub: [] as OptionsClass[],
    destinationCity: [] as OptionsClass[],
    createdBy: [] as OptionsClass[],
    cargoTypes: [] as string[]
  };

  init() {
    const currentHub = getHubData();
    const defaultHub = currentHub?.hubId
      ? [
          new OptionsClass({
            name: currentHub.hubName,
            code: currentHub.hubId
          })
        ]
      : [];

    const currentUser = [
      new OptionsClass({
        name:
          AccountController.accountData.name ||
          AccountController.accountData.username,
        code: String(AccountController.accountData.account_id)
      })
    ];

    this.form = {
      periodeStart: moment()
        .subtract(1, "day")
        .toDate(),
      periodeEnd: moment().toDate(),
      periodeStartTime: moment()
        .subtract(1, "day")
        .utc()
        .startOf("day")
        .toDate(),
      periodeEndTime: moment()
        .utc()
        .endOf("day")
        .toDate(),
      selectedHub: defaultHub,
      destinationCity: [],
      createdBy: currentUser,
      cargoTypes: [...CargoController.cargoTypes]
    };

    this.getHubList();
    this.fetchCityDestination();
    this.fetchCreatedBy();
  }

  // filter Date Range
  setPeriode(value: Date[]) {
    if (value.every(Boolean)) {
      this.form.periodeStart = moment(value[0])
        .utc()
        .toDate();

      this.form.periodeEnd = moment(value[1])
        .utc()
        .toDate();

      // validate time
      this.onValidateTime();
    }
  }
  // =======================================
  // Helpers
  // =======================================

  private clampToToday(date: Date, type: "start" | "end"): Date {
    const today =
      type === "start" ? moment().startOf("day") : moment().endOf("day");

    return moment(date).isAfter(today) ? today.toDate() : date;
  }

  private normalizeDate(parsedDate: Date, type: "start" | "end"): Date {
    const normalized =
      type === "start"
        ? moment(parsedDate).startOf("day")
        : moment(parsedDate).endOf("day");

    return this.clampToToday(normalized.toDate(), type);
  }

  private revertInput(inputEl: HTMLInputElement, date: Date) {
    inputEl.value = formatLocalizedDate(date, "id");
  }

  // =======================================
  // START DATE BLUR
  // =======================================

  onBlurPeriodeStart(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const rawValue = inputEl.value;

    const parsedDate = parseFuzzyLocalizedDate(rawValue, "id");

    // ❌ Parse gagal → revert + stop
    if (!parsedDate) {
      this.revertInput(inputEl, this.form.periodeStart);
      return;
    }

    // ✅ Normalize + Clamp
    const startDate = this.normalizeDate(parsedDate, "start");

    this.form.periodeStart = startDate;

    // ✅ Jika start > end → auto adjust end
    if (moment(startDate).isAfter(this.form.periodeEnd)) {
      this.form.periodeEnd = moment(startDate)
        .endOf("day")
        .toDate();
    }

    // Normalize display
    this.revertInput(inputEl, startDate);

    // Always sync
    this.autoHighlightDateRange();
    this.onValidateTime();
  }

  // =======================================
  // END DATE BLUR
  // =======================================

  onBlurPeriodeEnd(e: Event) {
    const inputEl = e.target as HTMLInputElement;
    const rawValue = inputEl.value;

    const parsedDate = parseFuzzyLocalizedDate(rawValue, "id");

    // ❌ Parse gagal → revert + stop
    if (!parsedDate) {
      this.revertInput(inputEl, this.form.periodeEnd);
      return;
    }

    // ✅ Normalize + Clamp
    const normalizedEnd = this.normalizeDate(parsedDate, "end");

    let finalEndDate = normalizedEnd;

    // ✅ Jika end < start → auto correct ke end of start day
    if (moment(normalizedEnd).isBefore(this.form.periodeStart)) {
      finalEndDate = moment(this.form.periodeStart)
        .endOf("day")
        .toDate();
    }

    this.form.periodeEnd = finalEndDate;

    // Normalize display
    this.revertInput(inputEl, finalEndDate);

    // Always sync
    this.autoHighlightDateRange();
    this.onValidateTime();
  }

  autoHighlightDateRange() {
    const refs: any = this.$refs?.dateRangeFilter;
    if (!refs) return;

    refs.dates = {
      start: this.form.periodeStart,
      end: this.form.periodeEnd
    };
    refs.onDateSelected();
    refs.syncPanelDate();
  }
  get periodeStartFormatted() {
    return this.formatDate(this.form.periodeStart);
  }

  get periodeEndFormatted() {
    return this.formatDate(this.form.periodeEnd);
  }

  get periodeStartTime() {
    return new Date(
      this.form.periodeStartTime.getFullYear(),
      this.form.periodeStartTime.getMonth(),
      this.form.periodeStartTime.getDate(),
      this.form.periodeStartTime.getUTCHours(),
      this.form.periodeStartTime.getUTCMinutes()
    );
  }
  get periodeEndTime() {
    return new Date(
      this.form.periodeEndTime.getFullYear(),
      this.form.periodeEndTime.getMonth(),
      this.form.periodeEndTime.getDate(),
      this.form.periodeEndTime.getUTCHours(),
      this.form.periodeEndTime.getUTCMinutes()
    );
  }

  inputDateStartTime(param: Date) {
    const base = moment(this.form.periodeStart).utc();

    this.form.periodeStartTime = base
      .hour(param.getHours())
      .minute(param.getMinutes())
      .second(0)
      .millisecond(0)
      .toDate();
  }
  inputDateEndTime(param: Date) {
    const base = moment(this.form.periodeEnd).utc();

    this.form.periodeEndTime = base
      .hour(param.getHours())
      .minute(param.getMinutes())
      .second(59)
      .millisecond(999)
      .toDate();
  }

  onValidateTime() {
    const startDate = this.buildPayloadDate(
      this.form.periodeStart,
      this.form.periodeStartTime
    );
    const endDate = this.buildPayloadDate(
      this.form.periodeEnd,
      this.form.periodeEndTime
    );

    if (moment(startDate).isAfter(endDate)) {
      this.inputDateEndTime(this.periodeStartTime);
    }
  }

  formatDate(value: Date) {
    return value ? moment(value).format("DD MMMM YYYY") : "";
  }

  // Multiple Select Filter
  hubList: OptionsClass[] = [];
  tempHubList: OptionsClass[] = [];
  getHubList() {
    HubController.getHubList(
      new RequestHubList({
        city:
          AccountController.accountData.account_type_detail?.partnerLocation
            ?.city_code,
        status: "active",
        partnerId: AccountController.accountData.account_type_detail?.id
      })
    ).then((res: HubList) => {
      this.hubList = res.data.map(
        (item: Hub) =>
          new OptionsClass({
            name: item.hubName,
            code: String(item.hubId)
          })
      );

      this.tempHubList = cloneDeep(this.hubList);
    });
  }
  onFilterHub(searchText: string) {
    const text = searchText.trim().toLowerCase();

    if (text) {
      this.hubList = this.tempHubList.filter((item: OptionsClass) =>
        item.name.toLowerCase().includes(text)
      );
      return;
    }

    this.getHubList();
  }

  onChangeHub() {
    this.form.createdBy = [];
  }

  // destination city
  cityDestinationData = [] as any[];
  tempCityDestinationData = [] as any[];
  async fetchCityDestination() {
    LocationController.getCityList({
      page: 1,
      limit: 1000,
      search: "",
      status: ""
    }).then((res) => {
      this.cityDestinationData = this.formatListCityDestination(res.cityData);

      this.tempCityDestinationData = cloneDeep(this.cityDestinationData);
    });
  }
  formatListCityDestination(data: any[]) {
    return data
      .filter((item) => item.name)
      .map(
        (item) =>
          new OptionsClass({ ...item, name: `${item.code} - ${item.name}` })
      );
  }
  onFilterCityDestination(searchText: string) {
    const text = searchText.trim().toLowerCase();

    if (text) {
      this.cityDestinationData = this.tempCityDestinationData.filter(
        (item: any) =>
          item.name.toLowerCase().includes(text) ||
          item.code.toLowerCase().includes(text)
      );
      return;
    }

    this.cityDestinationData = this.formatListCityDestination(
      LocationController.cityData.cityData
    );
  }

  // User pembooking
  createdByList: OptionsClass[] = [];
  tempCreatedByList: OptionsClass[] = [];

  async fetchCreatedBy() {
    const hubIds =
      this.form.selectedHub.length === this.tempHubList.length
        ? ""
        : this.form.selectedHub.map((item) => item.code).join(",");

    const account = AccountController.accountData;
    const reference =
      account?.account_type_detail?.id != null
        ? String(account.account_type_detail.id)
        : "";
    UserController.getUserListV4(
      new RequestListUser({
        page: 1,
        limit: 1000,
        version: "4",
        status: "active",
        hub_ids: hubIds,
        reference,
        account_type: account?.account_type ?? ""
      })
    ).then((res: UserEntities) => {
      this.createdByList = res.data.map((item: UserData) =>
        new OptionsClass({
          name: item.account_name || item.username,
          code: String(item.account_id)
        })
      );
      this.tempCreatedByList = cloneDeep(this.createdByList);
    });
  }

  private debouncedSearchCreatedBy = debounce((search: string) => {
    const hubIds =
      this.form.selectedHub.length === this.tempHubList.length
        ? ""
        : this.form.selectedHub.map((item) => item.code).join(",");
    const account = AccountController.accountData;
    const reference =
      account?.account_type_detail?.id != null
        ? String(account.account_type_detail.id)
        : "";
    UserController.getUserList(
      new RequestListUser({
        page: 1,
        limit: 50,
        version: "3",
        status: "active",
        hub_ids: hubIds,
        reference,
        account_type: account?.account_type ?? "",
        search
      })
    ).then((res: UserEntities) => {
      const mapped = res.data.map((item: UserData) =>
        new OptionsClass({
          name: item.account_name || item.username,
          code: String(item.account_id)
        })
      );
      this.createdByList = mapped;
      this.tempCreatedByList = cloneDeep(mapped);
    });
  }, 250);

  onFilterCreatedBy(searchText: string) {
    const raw = searchText || "";
    const text = raw.trim();

    if (!text.length || text.length <= 2) {
      this.fetchCreatedBy();
      return;
    }
    this.debouncedSearchCreatedBy(text);

    return;
  }

  buildPayloadDate(date: Date, time: Date): string {
    const localDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes(),
      0,
      0
    );

    const padZero = (num: number) => String(num).padStart(2, "0");

    return `${localDateTime.getFullYear()}-${padZero(
      localDateTime.getMonth() + 1
    )}-${padZero(localDateTime.getDate())}T${padZero(
      localDateTime.getUTCHours()
    )}:${padZero(localDateTime.getUTCMinutes())}:00.000Z`;
  }

  async onDownload() {
    try {
      MainAppController.showLoading();
      const startLocal = this.buildPayloadDate(
        this.form.periodeStart,
        this.form.periodeStartTime
      );

      const endLocal = this.buildPayloadDate(
        this.form.periodeEnd,
        this.form.periodeEndTime
      );

      const hubIds =
        this.form.selectedHub.length === this.tempHubList.length
          ? ""
          : this.form.selectedHub?.map((item) => item.code).join(",");

      const destinationCities =
        this.form.destinationCity.length === this.tempCityDestinationData.length
          ? ""
          : this.form.destinationCity.map((item) => item.code).join(",");

      const createdBy =
        this.form.createdBy.length === this.tempCreatedByList.length
          ? []
          : this.form.createdBy.map((item) => Number(item.code));

      const isAllCargoTypesSelected =
        this.form.cargoTypes.length === this.modaOptions.length;

      const cargoTypes =
        !this.form.cargoTypes.length || isAllCargoTypesSelected
          ? ""
          : this.form.cargoTypes.join(",");

      await CargoController.downloadListCargoSuccess(
        new DownloadCargoListApiRequest({
          startDate: startLocal,
          endDate: endLocal,
          hubIds,
          destinationCities,
          createdBy,
          cargoTypes
        })
      );
      this.onClose();
      ReportController.setOpenDownloadRequest(true);

      ReportController.listDownloadReport.pagination.page = 1;
      ReportController.listDownloadReport.pagination.limit = 20;
      ReportController.onGetListDownloadReport("cargo-report");
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(error, "Download Gagal !", () => {
          MainAppController.closeErrorMessage();
        })
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  get disabled() {
    return (
      !this.form.periodeStart ||
      !this.form.periodeEnd ||
      moment(this.form.periodeStart).isAfter(this.form.periodeEnd)
    );
  }
}
